import seeder from '@cleverbeagle/seeder';
import { Meteor } from 'meteor/meteor';
import { cloneDeep, find, forEach, map } from 'lodash';

import Annotations from '../../api/Annotations/Annotations';
import * as annotationOps from '../../api/Annotations/annotation-operations';
import AnnotationTemplates from '../../api/AnnotationTemplates/AnnotationTemplates';
import * as annotationTemplateOps from '../../api/AnnotationTemplates/annotation-template-operations';
import Projects from '../../api/Projects/Projects';
import * as projectOps from '../../api/Projects/project-operations';
import Videos from '../../api/Videos/Videos';
import * as videoOps from '../../api/Videos/video-operations';

import annotationExamples from './fixtures-examples/annotations';
import annotationTemplateExamples from './fixtures-examples/annotationTemplates';
import projectExamples from './fixtures-examples/projects';
import videoExamples from './fixtures-examples/videos';

const adminRoles = ['admin', 'premium-user'];

const addOwnerForStaticData = (staticData, userId) => {
  const examplesWithOwner = staticData.map(item => {
    const itemWithID = cloneDeep(item);
    itemWithID.owner = userId;
    return itemWithID;
  });
  return examplesWithOwner;
};

const replaceProjectsAndAnnotationTemplatesWithIds = (videos, userId) => {
  const userProjects = Projects.find({ owner: userId }).fetch();
  const allAnnotationTemplates = AnnotationTemplates.find({}).fetch();

  return map(videos, video => {
    const videoWithId = cloneDeep(video);

    const projectName = videoOps.getProject(video);
    const project = find(userProjects, ['name', projectName]);
    const projectId = projectOps.getId(project);
    videoWithId.project = projectId;

    const annotationTemplateNames = videoOps.getAnnotationTemplates(video);
    const annotationTemplates = map(annotationTemplateNames, annotationTemplateName =>
      find(allAnnotationTemplates, ['name', annotationTemplateName])
    );
    videoWithId.annotationTemplates = annotationTemplates;

    return videoWithId;
  });
};

const replaceVideosAndAnnotationTemplatesWithIds = (annotations, userId) => {
  const userVideos = Videos.find({ owner: userId }).fetch();
  const annotationTemplates = AnnotationTemplates.find({}).fetch();

  return map(annotations, annotation => {
    const annotationWithId = cloneDeep(annotation);
    annotationWithId.geoJSON.properties.owner = userId;

    const videoName = annotationOps.getVideo(annotation);
    const video = find(userVideos, ['name', videoName]);
    const videoId = videoOps.getId(video);
    annotationWithId.geoJSON.properties.video = videoId;

    const annotationTemplateName = annotationTemplateOps.getName(
      annotationOps.getTemplate(annotation)
    );
    const annotationTemplate = find(annotationTemplates, ['name', annotationTemplateName]);
    const annotationTemplateId = annotationTemplateOps.getId(annotationTemplate);
    const annotationTemplateCreatedAt = annotationTemplateOps.getCreation(annotationTemplate);
    const annotationTemplateUpdatedAt = annotationTemplateOps.getLastUpdate(annotationTemplate);
    annotationWithId.geoJSON.properties.template._id = annotationTemplateId;
    annotationWithId.geoJSON.properties.template.createdAt = annotationTemplateCreatedAt;
    annotationWithId.geoJSON.properties.template.updatedAt = annotationTemplateUpdatedAt;

    return annotationWithId;
  });
};

const seedAnnotations = (userId, resetCollection = false) => {
  const annotationsWithIds = replaceVideosAndAnnotationTemplatesWithIds(annotationExamples, userId);
  seeder(Annotations, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(annotationsWithIds, userId)
    }
  });
};

const seedAnnotationTemplates = (userId, resetCollection = false) => {
  seeder(AnnotationTemplates, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(annotationTemplateExamples, userId)
    }
  });
};

const seedProjects = (userId, resetCollection = false) => {
  seeder(Projects, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(projectExamples, userId)
    }
  });
};

const seedVideos = (userId, resetCollection = false) => {
  const videosWithIds = replaceProjectsAndAnnotationTemplatesWithIds(videoExamples, userId);
  seeder(Videos, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(videosWithIds, userId)
    }
  });
};

const updateAnnotationIds = userId => {
  const userAnnotations = Annotations.find({ owner: userId }).fetch();
  forEach(userAnnotations, annotation => {
    const annotationId = annotationOps.getId(annotation);
    Annotations.update(annotationId, { $set: { 'geoJSON.properties._id': annotationId } });
  });
};

seeder(Meteor.users, {
  resetCollection: false,
  seedIfExistingData: true,
  environments: ['development', 'staging'],
  data: {
    static: [
      {
        email: 'admin@geomotionvideo.com',
        password: 'password',
        profile: {
          name: {
            first: 'Admin',
            last: 'User'
          }
        },
        roles: adminRoles,
        dependentData(userId) {
          seedAnnotationTemplates(userId);
          seedProjects(userId);
          seedVideos(userId);
          seedAnnotations(userId);
          updateAnnotationIds(userId);
        }
      }
    ],
    dynamic: {
      count: 2,
      seed(iteration, faker) {
        return {
          email: `user+${iteration + 1}@geomotionvideo.com`,
          password: 'password',
          profile: {
            name: {
              first: faker.name.firstName(),
              last: faker.name.lastName()
            }
          },
          roles: ['free-user'],
          dependentData(userId) {
            seedProjects(userId);
            seedVideos(userId);
            seedAnnotations(userId);
            updateAnnotationIds(userId);
          }
        };
      }
    }
  }
});

seeder(Meteor.users, {
  resetCollection: false,
  seedIfExistingData: true,
  environments: ['production'],
  data: {
    static: [
      {
        email: 'admin@geomotionvideo.com',
        password: 'srmConsulting010',
        profile: {
          name: {
            first: 'Admin',
            last: 'User'
          }
        },
        roles: adminRoles,
        dependentData(userId) {
          seedAnnotationTemplates(userId);
          seedProjects(userId);
          seedVideos(userId);
          seedAnnotations(userId);
          updateAnnotationIds(userId);
        }
      }
    ]
  }
});

const userExists = Meteor.users.findOne({ 'emails.0.address': 'admin@geomotionvideo.com' });

if (!userExists.emails[0].verified) {
  Meteor.users.update(
    { _id: userExists._id },
    {
      $set: {
        'emails.0.verified': true,
        'storageCuota.sizeLimit': 10 ** 9
      }
    }
  );
}
